import {
  findHazardRowInAsset,
  MAPBOX_DEFAULT_STYLE_URL,
  parseMapboxLngLat,
  type BuxusOperatorPayload,
} from '@buxus/shared'
import type { HazardMapView } from './Map'

function read(o: Record<string, unknown>, k: string): unknown {
  return o[k]
}

function collectAssets(payload: BuxusOperatorPayload): Record<string, unknown>[] {
  const op = payload.operator as Record<string, unknown> | undefined
  const fromOp = op?.assets ?? op?.Assets
  if (Array.isArray(fromOp)) return fromOp as Record<string, unknown>[]
  const root = payload as unknown as Record<string, unknown>
  return Array.isArray(root.assets) ? (root.assets as Record<string, unknown>[]) : []
}

/** Style URL fallback for hazard storage maps when the asset lacks `mapboxLink`. */
export function hazardStorageFallbackStyleUrl(operator: Record<string, unknown> | undefined): string {
  const v =
    operator &&
    (typeof read(operator, 'mapboxLink') === 'string'
      ? String(read(operator, 'mapboxLink')).trim()
      : typeof read(operator, 'MapboxLink') === 'string'
        ? String(read(operator, 'MapboxLink')).trim()
        : '')
  if (
    v &&
    (v.startsWith('mapbox://styles/') || v.startsWith('https://'))
  )
    return v
  return MAPBOX_DEFAULT_STYLE_URL
}

function hazardMapFromRow(
  h: Record<string, unknown>,
  asset: Record<string, unknown>,
  operator: Record<string, unknown>,
): HazardMapView | null {
  const mapboxAsset = read(asset, 'mapboxLink') ?? read(asset, 'MapboxLink')
  if (typeof mapboxAsset !== 'string' || !mapboxAsset.trim()) return null

  const rawLat = read(h, 'storageLat') ?? read(h, 'StorageLat')
  const rawLng = read(h, 'storageLong') ?? read(h, 'StorageLong')
  const rawZoom = read(h, 'storageZoomLevel') ?? read(h, 'StorageZoomLevel')

  let z = typeof rawZoom === 'number' && Number.isFinite(rawZoom) ? rawZoom : Number(rawZoom)
  if (!Number.isFinite(z)) z = 12
  z = Math.min(22, Math.max(0, z))

  const center = parseMapboxLngLat(
    rawLat as string | number | null | undefined,
    rawLng as string | number | null | undefined,
    { rejectOrigin: true },
  )
  if (!center) return null

  const styleUrlCandidate = mapboxAsset.trim()
  const styleUrl =
    styleUrlCandidate.startsWith('mapbox://styles/') ||
    styleUrlCandidate.startsWith('https://')
      ? styleUrlCandidate
      : hazardStorageFallbackStyleUrl(operator)

  const nm =
    read(h, 'storageName') ??
    read(h, 'StorageName') ??
    read(h, 'sDSTitle') ??
    read(h, 'SDSTitle')
  const markerLabel =
    typeof nm === 'string' && nm.trim() ? nm.trim() : 'Hazard storage location'

  return { styleUrl, center, zoom: z, markerLabel }
}

/** Hazard storage map — by `hi` (preferred) or first row matching `hp` product subtype. */
export function findHazardStorageMapView(params: {
  payload: BuxusOperatorPayload | null | undefined
  assetId: string | null
  productSubtype?: string | null
  hazardId?: string | null
}): HazardMapView | null {
  const { payload, assetId, productSubtype, hazardId } = params
  if (!payload?.operator || !assetId?.trim()) return null
  const wantAsset = assetId.trim()
  const wantId = hazardId?.trim() ?? ''
  const wantSub = productSubtype?.trim() ?? ''

  const operator = payload.operator as unknown as Record<string, unknown>
  let asset: Record<string, unknown> | null = null
  for (const row of collectAssets(payload)) {
    const id = row.id ?? row.Id
    if (typeof id === 'string' && id.trim() === wantAsset) {
      asset = row
      break
    }
  }
  if (!asset) return null

  if (wantId) {
    const hz = findHazardRowInAsset(asset, wantId)
    return hz ? hazardMapFromRow(hz, asset, operator) : null
  }

  if (!wantSub) return null

  const hazards = asset.hazards ?? asset.Hazards
  if (!Array.isArray(hazards)) return null

  for (const hz of hazards) {
    if (!hz || typeof hz !== 'object') continue
    const h = hz as Record<string, unknown>
    const product = read(h, 'product') ?? read(h, 'Product')
    if (!product || typeof product !== 'object') continue
    const p = product as Record<string, unknown>
    const sub = p.productSubType ?? p.ProductSubType
    if (typeof sub !== 'string' || sub.trim() !== wantSub) continue
    const view = hazardMapFromRow(h, asset, operator)
    if (view) return view
  }

  return null
}
